import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, TablePagination, Typography,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {
  CardWrapper, SectionTitle, Field, formatBRL,
} from '../../UI';
import CabecalhoFichaFinanceira from '../Financeira/CabecalhoFichaFinanceira';
import TableLancamentosSintetica from '../Financeira/TableLancamentosSintetica';
import OperacoesFichaFinanceira from '../Financeira/OperacoesFichaFinanceira';
import {
  somatoriosDevedor, somatoriosCliente, somatoriosHb,
} from '../Financeira/distribuicaoValoresFichaFinanceira.utils';
import * as C from '../../../theme/colors.constants';

const OPCOES_TAMANHO_PAGINA_LANCAMENTOS = [10, 15, 20];

/** Shape de um bloco já normalizado por `dadosPagamentoFactory` (produção). */
const dadosPagamentoShape = PropTypes.shape({
  idProcesso: PropTypes.number,
  valores: PropTypes.shape({
    valorSaldoPrincipal: PropTypes.number,
    valorMulta: PropTypes.number,
    valorProtesto: PropTypes.number,
    valorJurosCliente: PropTypes.number,
    valorComissaoPagaCliente: PropTypes.number,
    valorJurosABE: PropTypes.number,
    valorHonorarios: PropTypes.number,
    valorComercial: PropTypes.number,
    valorAdvogado: PropTypes.number,
    valorNegociador: PropTypes.number,
    valorDSR: PropTypes.number,
    valorPago: PropTypes.number,
  }),
});

/**
 * Aba Financeira.
 *
 * Print `grid_check_financeira.png`: Ficha Financeira no topo, Lançamentos no
 * meio, e três cards no rodapé (Saldos Atuais, Totais Já Pagos, Operações).
 */
function CardResumo({ titulo, icone, linhas }) {
  return (
    <CardWrapper>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
      }}
      >
        {icone}
        <Typography variant="h3">{titulo}</Typography>
      </Box>
      {linhas.map(l => (
        <Box
          key={l.label}
          sx={{
            display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4,
          }}
        >
          <Typography variant="body2">{l.label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: C.navy }}>{l.valor}</Typography>
        </Box>
      ))}
    </CardWrapper>
  );
}
CardResumo.propTypes = { titulo: PropTypes.string, icone: PropTypes.node, linhas: PropTypes.array };
CardResumo.defaultProps = { titulo: null, icone: null, linhas: [] };

/**
 * "Ausente" para um valor vindo de service/ProcessoDTO: `null`/`undefined`
 * OU string vazia/só espaço — `Number('')` é `0`, não `NaN`, então sem esse
 * guard explícito uma string vazia passaria como "zero genuíno" em vez de
 * "sem dado". Compartilhado por todo formatter deste arquivo que decide
 * "—" vs. valor de negócio real, para essa checagem nunca divergir entre
 * campos irmãos do mesmo ProcessoDTO.
 */
function isVazio(valor) {
  return valor === null || valor === undefined
    || (typeof valor === 'string' && valor.trim() === '');
}

/**
 * Formata um BigDecimal (chega como number ou string, conforme serialização
 * do backend) como percentual pt-BR com 2 casas — mesma tolerância a tipo já
 * aplicada em outros campos numéricos vindos de service (ex.: `codigoCliente`
 * em `CampoIdentificacao`). "—" quando ausente ou não numérico.
 */
function formatPercentual(valor) {
  const numero = Number(valor);
  if (isVazio(valor) || Number.isNaN(numero)) {
    return '—';
  }
  return `${numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/**
 * `idTipoRegraCalculoRepasse` não é um percentual — é um código de regra de
 * cálculo (Cadastro do Cliente): `1` = repasse único, qualquer outro valor
 * numérico = não. "—" quando ausente ou não numérico, nunca "Não" por
 * omissão (mesma armadilha de `formatPercentual` — string vazia não é um
 * "Não" real, é falta de dado).
 */
function formatRepasseUnico(idTipoRegraCalculoRepasse) {
  const numero = Number(idTipoRegraCalculoRepasse);
  if (isVazio(idTipoRegraCalculoRepasse) || Number.isNaN(numero)) {
    return '—';
  }
  return numero === 1 ? 'Sim' : 'Não';
}

export default function AbaFinanceira({
  processo, dadosProcesso, dadosDistribuicaoValores, lancamentos, lancamentosTotal,
  lancamentosPagina, lancamentosTamanhoPagina, lancamentosOrdenacao,
  onChangeOrdenacaoLancamentos, onChangePaginaLancamentos, onChangeTamanhoPaginaLancamentos,
}) {
  const { cardPagamento, cardPagamentoDireto, fichaFinanceiraInicio } = dadosDistribuicaoValores ?? {};
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.8vw, 22px)' }}>
      <CardWrapper>
        <SectionTitle>Ficha Financeira</SectionTitle>
        <Box sx={{ mb: 2 }}>
          <CabecalhoFichaFinanceira processo={processo} dadosProcesso={dadosProcesso} />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 'clamp(10px, 0.8vw, 22px)',
          }}
        >
          {/* Juros Simples / Repasse ao Cliente único (AW2-1077 Etapa 2) —
              fonte real: `porcentagemJurosRepasse`/`idTipoRegraCalculoRepasse`,
              campos do próprio ProcessoDTO retornado por
              `/processos/pesquisar` (mesmo objeto que já popula `processo`
              via `controleProcessosPage.mapper.js`) — sem fetch adicional
              nesta aba. `idTipoRegraCalculoRepasse` não é percentual: é um
              código de regra (1 = repasse único = "Sim", qualquer outro
              valor = "Não"), ver `formatRepasseUnico`. */}
          <Field
            label="Juros Simples - Repasse ao Cliente (%)"
            value={formatPercentual(processo?.porcentagemJurosRepasse)}
            InputProps={{ readOnly: true }}
          />
          <Field
            label="Repasse ao Cliente Único:"
            value={formatRepasseUnico(processo?.idTipoRegraCalculoRepasse)}
            InputProps={{ readOnly: true }}
          />
        </Box>
      </CardWrapper>

      <CardWrapper>
        <SectionTitle>Lançamentos</SectionTitle>
        {/*
          Diferente de Títulos/Antecedentes (que escondem a grid atrás de um
          texto "Carregando.../Não encontrado" quando vazios), esta grid fica
          SEMPRE visível, mesmo com 0 linhas — exigência explícita da AC de
          AW2-1078 (CP-01 "Tela vazia": grid exibida vazia, sem nenhuma
          linha, nunca substituída por mensagem). `lancamentos` começa
          `null` (hook ainda não resolveu o 1º fetch) — `?? []` evita passar
          `null` para o componente de tabela.
        */}
        <Box sx={{ overflowX: 'auto' }}>
          <TableLancamentosSintetica
            lancamentos={lancamentos ?? []}
            ordenacao={lancamentosOrdenacao}
            onChangeOrdenacao={onChangeOrdenacaoLancamentos}
          />
        </Box>
        {lancamentosTotal > 0 && (
          <TablePagination
            component="div"
            count={lancamentosTotal}
            page={lancamentosPagina}
            rowsPerPage={lancamentosTamanhoPagina}
            rowsPerPageOptions={OPCOES_TAMANHO_PAGINA_LANCAMENTOS}
            onPageChange={(event, novaPagina) => onChangePaginaLancamentos(novaPagina)}
            onRowsPerPageChange={onChangeTamanhoPaginaLancamentos}
            labelRowsPerPage="Linhas por página:"
          />
        )}
      </CardWrapper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          alignItems: 'start',
        }}
      >
        <CardResumo
          titulo="Saldos Atuais"
          icone={<BarChartIcon sx={{ color: C.navy }} />}
          linhas={[
            { label: 'Devedor:', valor: formatBRL(somatoriosDevedor(fichaFinanceiraInicio)) },
            { label: 'Cliente:', valor: formatBRL(somatoriosCliente(fichaFinanceiraInicio)) },
            { label: 'H.B.:', valor: formatBRL(somatoriosHb(fichaFinanceiraInicio)) },
          ]}
        />
        <CardResumo
          titulo="Totais Já Pagos"
          icone={<AttachMoneyIcon sx={{ color: C.navy }} />}
          linhas={[
            {
              label: 'Devedor:',
              valor: formatBRL(somatoriosDevedor(cardPagamento) + somatoriosDevedor(cardPagamentoDireto)),
            },
            {
              label: 'Cliente:',
              valor: formatBRL(somatoriosCliente(cardPagamento) + somatoriosCliente(cardPagamentoDireto)),
            },
            {
              label: 'HB:',
              valor: formatBRL(somatoriosHb(cardPagamento) + somatoriosHb(cardPagamentoDireto)),
            },
          ]}
        />
        <OperacoesFichaFinanceira dadosProcesso={dadosProcesso} idProcesso={processo?.id} />
      </Box>
    </Box>
  );
}
AbaFinanceira.propTypes = {
  processo: PropTypes.object,
  dadosProcesso: PropTypes.object,
  dadosDistribuicaoValores: PropTypes.shape({
    cardPagamento: dadosPagamentoShape,
    cardPagamentoDireto: dadosPagamentoShape,
    fichaFinanceiraInicio: dadosPagamentoShape,
  }),
  lancamentos: PropTypes.array,
  lancamentosTotal: PropTypes.number,
  lancamentosPagina: PropTypes.number,
  lancamentosTamanhoPagina: PropTypes.number,
  lancamentosOrdenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  onChangeOrdenacaoLancamentos: PropTypes.func,
  onChangePaginaLancamentos: PropTypes.func,
  onChangeTamanhoPaginaLancamentos: PropTypes.func,
};
AbaFinanceira.defaultProps = {
  processo: null,
  dadosProcesso: null,
  dadosDistribuicaoValores: null,
  lancamentos: [],
  lancamentosTotal: 0,
  lancamentosPagina: 0,
  lancamentosTamanhoPagina: 10,
  lancamentosOrdenacao: { campo: 'dataLancamento', direcao: 'DESC' },
  onChangeOrdenacaoLancamentos: null,
  onChangePaginaLancamentos: null,
  onChangeTamanhoPaginaLancamentos: null,
};
